// ============================================================================
// PanicBackButton — global "I'm stuck, get me home" floating button.
// ============================================================================
// Shows a soft, always-visible button at the bottom-left of any full-screen
// internal page (Design Studio, Field Mode, etc.). One tap → /dashboard.
// Shannon's safety net: she never has to use the browser back button and risk
// hitting the "this site can't be reached" page on mobile.
//
// Usage:
//   import PanicBackButton from "@/components/PanicBackButton";
//   ...
//   return (<>
//     <YourPageContent />
//     <PanicBackButton />
//   </>);
//
// Props:
//   to         — destination route (default: "/dashboard")
//   label      — visible text (default: "Dashboard")
//   show       — boolean override; defaults to always-on
// ============================================================================
import React from "react";
import { useNavigate } from "react-router-dom";

const PanicBackButton = ({ to = "/dashboard", label = "Dashboard", show = true }) => {
  const navigate = useNavigate();
  if (!show) return null;
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      data-testid="panic-back-button"
      aria-label={`Back to ${label}`}
      className="panic-back-btn"
      style={{
        position: "fixed",
        bottom: 18,
        left: 18,
        zIndex: 999,
        padding: "11px 16px 11px 14px",
        borderRadius: 999,
        border: "1px solid rgba(232,184,196,0.55)",
        background: "linear-gradient(135deg, rgba(15,20,30,0.92) 0%, rgba(20,15,35,0.92) 100%)",
        color: "#E8B8C4",
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: "0.02em",
        cursor: "pointer",
        boxShadow: "0 8px 22px rgba(0,0,0,0.45), 0 0 0 1px rgba(232,184,196,0.08)",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        transition: "transform 0.15s, box-shadow 0.15s",
        WebkitTapHighlightColor: "transparent",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 10px 26px rgba(0,0,0,0.55), 0 0 0 1px rgba(232,184,196,0.18)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 8px 22px rgba(0,0,0,0.45), 0 0 0 1px rgba(232,184,196,0.08)";
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 15, lineHeight: 1 }}>←</span>
      {label}
    </button>
  );
};

export default PanicBackButton;
